/**
 * Clarification Protocol for Gibbs Text-to-Action
 *
 * Generates structured clarification responses when an action request
 * is missing parameters, has ambiguous intent, or has unmet preconditions.
 *
 * Output format (per D5):
 * {
 *   intent: "clarify",
 *   question: string,
 *   options: [{label, value}],
 *   missing: string[],
 *   context: {actionId, partialParams}
 * }
 *
 * @module gibbs/text-to-action/clarification
 */

import { checkPreconditions } from '../registry/index';

// ─── Precondition-to-resolution mapping ────────────────────────────────────

/**
 * Maps precondition check IDs to human-readable resolution suggestions.
 *
 * **Important:** these resolvers must be "self-contained" actions that can
 * be auto-dispatched without additional user input. `load-compound`
 * requires the user to supply mol data (SMILES / SDF / etc.) and therefore
 * is NOT a valid auto-resolver — it would need its own clarification
 * round to gather the molecule. `load-starter-compounds` takes only the
 * active caseData and loads the co-crystallized reference ligands, so
 * it works as a one-click resolver.
 *
 * This table is the single source of truth for clarification-path
 * precondition resolution. It must stay in sync with
 * PRECONDITION_RESOLVERS in planActions.js — any divergence between
 * the two means the clarify-and-resolve path and the plan-auto-insert
 * path will emit different actions for the same unmet precondition.
 */
const PRECONDITION_RESOLUTIONS = {
    protein_loaded: { label: 'Load a protein first', actionId: 'load-protein-from-pdb' },
    compound_loaded: { label: 'Load reference compounds first', actionId: 'load-starter-compounds' },
    fragment_map_available: { label: 'Load a fragment map first', actionId: 'load-fragment-map' },
    binding_site_defined: { label: 'Define a binding site first', actionId: null },
    active_compound_set: { label: 'Select an active compound', actionId: null },
    compound_has_3d: { label: 'Load reference compounds (3D)', actionId: 'load-starter-compounds' },
    compound_has_energies: { label: 'Calculate energies first', actionId: 'get-energies' },
    jobman_available: { label: 'Job manager is not available', actionId: null },
    fragment_data_available: { label: 'Fragment data service is not available', actionId: null },
};

// ─── Option generation helpers ─────────────────────────────────────────────

/**
 * Generate options for a compound-type parameter from workspace state.
 *
 * @param {Object} workspaceState - Serialized workspace state
 * @returns {{label: string, value: *}[]}
 */
function compoundOptionsFromState(workspaceState) {
    const options = [];
    const compounds = workspaceState?.compounds || [];

    if (workspaceState?.activeCompound != null) {
        const active = compounds.find((c) => c.index === workspaceState.activeCompound);
        if (active) {
            options.push({
                label: `Active compound (${active.name})`,
                value: { cmpdIndex: active.index },
            });
        }
    }

    for (const c of compounds) {
        if (c.index === workspaceState?.activeCompound) continue; // already listed
        options.push({
            label: c.name || `Compound ${c.index}`,
            value: { cmpdIndex: c.index },
        });
    }

    if (compounds.length > 1) {
        options.push({ label: 'All compounds', value: 'all' });
    }

    return options;
}

/**
 * Generate options for a parameter based on its ParamDef and workspace state.
 *
 * @param {import('../registry/schema.js').ParamDef} paramDef
 * @param {Object} workspaceState
 * @returns {{label: string, value: *}[]}
 */
function generateParamOptions(paramDef, workspaceState) {
    // Enum options from ParamDef
    if (paramDef.options && paramDef.options.length > 0) {
        const opts = paramDef.options.map((opt) => ({
            label: opt,
            value: opt,
        }));
        // Highlight default if present
        if (paramDef.defaultValue != null) {
            const defIdx = opts.findIndex((o) => o.value === paramDef.defaultValue);
            if (defIdx >= 0) {
                opts[defIdx].label += ' (default)';
            }
        }
        return opts;
    }

    // Compound-type parameters: suggest from workspace
    const compoundTypes = ['CompoundList', 'Compound', 'CompoundRef'];
    if (compoundTypes.includes(paramDef.type)) {
        return compoundOptionsFromState(workspaceState);
    }

    // Protein-type parameters: suggest from workspace
    if (paramDef.type === 'CaseData' || paramDef.type === 'Protein') {
        const proteins = workspaceState?.proteins || [];
        return proteins.map((p, i) => ({
            label: p.name || p.pdbId || `Protein ${i + 1}`,
            value: p.pdbId,
        }));
    }

    // Boolean with a default
    if (paramDef.type === 'boolean') {
        return [
            { label: 'Yes', value: true },
            { label: 'No', value: false },
        ];
    }

    // No smart options available
    return [];
}

/**
 * Build a human-readable question for a missing parameter.
 *
 * @param {import('../registry/schema.js').ParamDef} paramDef
 * @param {import('../registry/schema.js').ActionDef} actionDef
 * @returns {string}
 */
function questionForMissingParam(paramDef, actionDef) {
    // Use the param description to form a natural question
    if (paramDef.options && paramDef.options.length > 0) {
        return `Which ${paramDef.name} would you like for ${actionDef.name}? Options: ${paramDef.options.join(', ')}`;
    }
    return `What ${paramDef.description || paramDef.name} would you like for ${actionDef.name}?`;
}

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Generate a structured clarification object for missing or invalid parameters.
 *
 * @param {import('../registry/schema.js').ActionDef} actionDef - The target action
 * @param {string[]} missingParams - Names of missing/invalid parameters
 * @param {Object} workspaceState - Serialized workspace state
 * @param {Object} [partialParams={}] - Parameters already extracted
 * @returns {Object} Clarification object per D5 format
 */
export function generateClarification(
    actionDef, missingParams, workspaceState, partialParams = {}
) {
    if (!actionDef) {
        return {
            intent: 'clarify',
            question: 'I could not identify which action you want to perform. Could you rephrase?',
            options: [],
            missing: [],
            context: { actionId: null, partialParams },
        };
    }

    // Check preconditions first -- unmet preconditions take priority
    const precondResult = checkPreconditions(actionDef.id, workspaceState);
    if (!precondResult.met) {
        return generatePreconditionClarification(actionDef, precondResult.unmet, partialParams);
    }

    // Find ParamDefs for the missing parameter names (check both inputs and parameters)
    const allParams = [...actionDef.inputs, ...actionDef.parameters];
    const missingDefs = missingParams
        .map((name) => allParams.find((p) => p.name === name))
        .filter(Boolean);

    // Focus the question on the first missing param (most important)
    const primaryMissing = missingDefs[0];
    const question = primaryMissing
        ? questionForMissingParam(primaryMissing, actionDef)
        : `I need more information to perform ${actionDef.name}. What parameters would you like to use?`;

    const options = primaryMissing
        ? generateParamOptions(primaryMissing, workspaceState)
        : [];

    return {
        intent: 'clarify',
        question,
        options,
        missing: missingParams,
        context: {
            actionId: actionDef.id,
            partialParams,
        },
    };
}

/**
 * Generate a clarification for unmet preconditions.
 *
 * @param {import('../registry/schema.js').ActionDef} actionDef
 * @param {string[]} unmetPreconditions - Check IDs that failed
 * @param {Object} partialParams
 * @returns {Object} Clarification object
 */
function generatePreconditionClarification(actionDef, unmetPreconditions, partialParams) {
    // Build human-readable description of what's needed
    const precondDescriptions = actionDef.preconditions
        .filter((p) => unmetPreconditions.includes(p.check))
        .map((p) => p.description);

    const question = precondDescriptions.length > 0
        ? `Before ${actionDef.name}: ${precondDescriptions.join('; ')}.`
        : `${actionDef.name} requires setup that is not yet complete.`;

    // Generate resolution options
    const options = unmetPreconditions
        .map((checkId) => PRECONDITION_RESOLUTIONS[checkId])
        .filter(Boolean)
        .map((res) => ({
            label: res.label,
            value: res.actionId ? { resolveAction: res.actionId } : null,
        }))
        .filter((o) => o.value != null);

    return {
        intent: 'clarify',
        question,
        options,
        missing: [],
        context: {
            actionId: actionDef.id,
            partialParams,
            unmetPreconditions,
        },
    };
}

/**
 * Generate a clarification for ambiguous intent (multiple candidate actions).
 *
 * @param {import('../registry/schema.js').ActionDef[]} candidateActions - Possible matching actions
 * @param {string} userUtterance - The original user request
 * @returns {Object} Clarification object
 */
export function generateAmbiguousClarification(candidateActions, userUtterance) {
    const options = candidateActions.map((action) => ({
        label: `${action.name} — ${action.description.split('.')[0]}`,
        value: { actionId: action.id },
    }));

    return {
        intent: 'clarify',
        question: `I found multiple actions that could match "${userUtterance}". Which did you mean?`,
        options,
        missing: [],
        context: {
            actionId: null,
            partialParams: {},
            candidates: candidateActions.map((a) => a.id),
        },
    };
}
